import React, { useState } from 'react';
import { ArrowLeft, Check, Zap, Users, Building, Sparkles, Building2, Crown, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CostCalculator from './CostCalculator';
import MarketingShell from './MarketingShell';

interface PricingPageProps {
    onBack?: () => void;
    onOpenAuthModal?: (mode: 'login' | 'signup') => void;
}

const PricingPage: React.FC<PricingPageProps> = ({ onBack, onOpenAuthModal }) => {
    const { t } = useTranslation('legal');
    const [activeTab, setActiveTab] = useState<'individual' | 'enterprise'>('individual');

    const individualPlans = [
        {
            id: 'trial',
            icon: Sparkles,
            popular: false,
            requiresCreditCard: true,
        },
        {
            id: 'personal_shadow',
            icon: Users,
            popular: false,
        },
        {
            id: 'delegate_replica',
            icon: Zap,
            popular: true,
        },
        {
            id: 'full_replica',
            icon: Building,
            popular: false,
            requiresApplication: true,
        },
    ];

    const enterprisePlans = [
        {
            id: 'team_starter',
            icon: Users,
            popular: false,
        },
        {
            id: 'business',
            icon: Building2,
            popular: true,
        },
        {
            id: 'enterprise_custom',
            icon: Crown,
            popular: false,
            isContactSales: true,
        },
    ];

    const activePlans = activeTab === 'individual' ? individualPlans : enterprisePlans;

    const handleCTA = (planId: string) => {
        if (planId === 'full_replica') {
            alert('Full Replica requires application. Application modal coming soon.');
            return;
        }
        if (planId === 'enterprise_custom') {
            alert('Contact sales: sales@humanreplica.ai');
            return;
        }
        if (onOpenAuthModal) {
            onOpenAuthModal('signup');
        }
    };

    const card = 'bg-white/[0.04] backdrop-blur-sm border border-white/[0.06] rounded-2xl';

    return (
        <MarketingShell>
            {/* Header */}
            <div className="border-b border-white/[0.06] bg-white/[0.02]">
                <div className="max-w-6xl mx-auto px-6 py-12">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 text-sm mb-8 transition-colors"
                        >
                            <ArrowLeft size={15} />
                            Back
                        </button>
                    )}
                    <div className="text-center">
                        <h1 className="text-4xl font-bold text-white mb-4">
                            {t('pricing_page.title')}
                        </h1>
                        <p className="text-xl text-white/50 max-w-2xl mx-auto mb-8">
                            {t('pricing_page.subtitle')}
                        </p>

                        {/* Individual / Enterprise Toggle */}
                        <div className="inline-flex items-center bg-white/[0.05] rounded-xl p-1 border border-white/[0.08]">
                            <button
                                onClick={() => setActiveTab('individual')}
                                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    activeTab === 'individual'
                                        ? 'bg-white/[0.10] text-white shadow-sm'
                                        : 'text-white/40 hover:text-white/60'
                                }`}
                            >
                                <span className="flex items-center gap-2">
                                    <Users size={16} />
                                    {t('pricing_page.individual_tab')}
                                </span>
                            </button>
                            <button
                                onClick={() => setActiveTab('enterprise')}
                                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    activeTab === 'enterprise'
                                        ? 'bg-white/[0.10] text-white shadow-sm'
                                        : 'text-white/40 hover:text-white/60'
                                }`}
                            >
                                <span className="flex items-center gap-2">
                                    <Building size={16} />
                                    {t('pricing_page.enterprise_tab')}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pricing Grid */}
            <div className="max-w-6xl mx-auto px-6 py-16">
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${
                    activeTab === 'individual' ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
                }`}>
                    {activePlans.map((plan) => {
                        const Icon = plan.icon;
                        const features = t(`pricing_page.plans.${plan.id}.features`, { returnObjects: true }) as string[];
                        const commission = activeTab === 'enterprise'
                            ? t(`pricing_page.plans.${plan.id}.commission`)
                            : null;

                        return (
                            <div
                                key={plan.id}
                                className={`${card} p-6 relative flex flex-col transition-transform hover:-translate-y-1 duration-200 ${
                                    plan.popular ? 'ring-2 ring-[#0A84FF]' : ''
                                }`}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                        <span className="bg-[#0A84FF] text-white text-xs font-semibold px-3 py-1 rounded-full">
                                            {activeTab === 'individual'
                                                ? t('pricing_page.most_popular')
                                                : t('pricing_page.most_popular_enterprise')
                                            }
                                        </span>
                                    </div>
                                )}

                                <div className="mb-4">
                                    <div className="w-10 h-10 rounded-lg bg-white/[0.06] flex items-center justify-center mb-3">
                                        <Icon size={20} className={plan.popular ? 'text-[#0A84FF]' : 'text-white/50'} />
                                    </div>
                                    <h3 className="text-xl font-semibold text-white">
                                        {t(`pricing_page.plans.${plan.id}.name`)}
                                    </h3>
                                    <p className="text-sm text-white/40 mt-1">
                                        {t(`pricing_page.plans.${plan.id}.description`)}
                                    </p>
                                </div>

                                <div className="mb-2">
                                    <span className="text-3xl font-bold text-white">
                                        {t(`pricing_page.plans.${plan.id}.price`)}
                                    </span>
                                    {t(`pricing_page.plans.${plan.id}.period`) && (
                                        <span className="text-white/40 text-sm">
                                            /{t(`pricing_page.plans.${plan.id}.period`)}
                                        </span>
                                    )}
                                </div>

                                {/* Annual price for enterprise */}
                                {activeTab === 'enterprise' && plan.id !== 'enterprise_custom' && (
                                    <p className="text-xs text-white/35 mb-4">
                                        {t(`pricing_page.plans.${plan.id}.price_annual`)} billed annually
                                    </p>
                                )}

                                {/* Commission badge for enterprise */}
                                {commission && (
                                    <div className="mb-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0A84FF]/10 text-[#0A84FF] text-xs font-medium w-fit">
                                        <Globe size={12} />
                                        {commission} {t('pricing_page.commission_label')}
                                    </div>
                                )}

                                <ul className="space-y-3 mb-4 flex-1">
                                    {Array.isArray(features) && features.map((feature, idx) => (
                                        <li key={idx} className="flex items-start gap-2">
                                            <Check size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm text-white/60">{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                {/* Credit Card Note for Trial */}
                                {'requiresCreditCard' in plan && plan.requiresCreditCard && (
                                    <p className="text-xs text-white/35 mb-4 italic">
                                        {t(`pricing_page.plans.${plan.id}.credit_card_note`)}
                                    </p>
                                )}

                                {/* Revenue Disclaimer for Full Replica */}
                                {plan.id === 'full_replica' && (
                                    <div className="mb-4 p-3 bg-white/[0.04] rounded-xl border border-white/[0.06]">
                                        <p className="text-xs text-white/50">
                                            {t(`pricing_page.plans.${plan.id}.revenue_disclaimer`)}
                                        </p>
                                    </div>
                                )}

                                {/* Replica cost note for enterprise */}
                                {activeTab === 'enterprise' && (
                                    <p className="text-xs text-white/35 mb-4 italic">
                                        {t('pricing_page.replica_cost_note')}
                                    </p>
                                )}

                                <button
                                    onClick={() => handleCTA(plan.id)}
                                    className={`w-full py-3 px-4 rounded-xl font-medium transition-colors ${
                                        plan.popular
                                            ? 'bg-[#0A84FF] text-white hover:bg-[#0076E6]'
                                            : 'bg-white/[0.06] text-white hover:bg-white/[0.10] border border-white/[0.08]'
                                    }`}
                                >
                                    {t(`pricing_page.plans.${plan.id}.cta`)}
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* Cost Calculator — only visible on Enterprise tab */}
                {activeTab === 'enterprise' && <CostCalculator />}

                {/* Trust Signals */}
                <div className="mt-16 text-center">
                    <p className="text-white/40 mb-4">{t('pricing_page.trusted_by')}</p>
                    <div className="flex justify-center items-center gap-8">
                        <span className="text-sm text-white/55">{t('footer.soc2')}</span>
                        <span className="text-white/20">•</span>
                        <span className="text-sm text-white/55">{t('footer.gdpr')}</span>
                        <span className="text-white/20">•</span>
                        <span className="text-sm text-white/55">{t('footer.iso')}</span>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="mt-20 max-w-3xl mx-auto">
                    <h2 className="text-2xl font-semibold text-white text-center mb-8">
                        {t('pricing_page.faq_title')}
                    </h2>
                    <div className="space-y-4">
                        <div className={`${card} p-5`}>
                            <h3 className="font-medium text-white mb-2">
                                {t('pricing_page.faq.q1.question')}
                            </h3>
                            <p className="text-sm text-white/55">
                                {t('pricing_page.faq.q1.answer')}
                            </p>
                        </div>
                        <div className={`${card} p-5`}>
                            <h3 className="font-medium text-white mb-2">
                                {t('pricing_page.faq.q2.question')}
                            </h3>
                            <p className="text-sm text-white/55">
                                {t('pricing_page.faq.q2.answer')}
                            </p>
                        </div>
                        <div className={`${card} p-5`}>
                            <h3 className="font-medium text-white mb-2">
                                {t('pricing_page.faq.q3.question')}
                            </h3>
                            <p className="text-sm text-white/55">
                                {t('pricing_page.faq.q3.answer')}
                            </p>
                        </div>
                        {/* Enterprise-specific FAQs */}
                        {activeTab === 'enterprise' && (
                            <>
                                <div className={`${card} p-5`}>
                                    <h3 className="font-medium text-white mb-2">
                                        {t('pricing_page.faq.q4.question')}
                                    </h3>
                                    <p className="text-sm text-white/55">
                                        {t('pricing_page.faq.q4.answer')}
                                    </p>
                                </div>
                                <div className={`${card} p-5`}>
                                    <h3 className="font-medium text-white mb-2">
                                        {t('pricing_page.faq.q5.question')}
                                    </h3>
                                    <p className="text-sm text-white/55">
                                        {t('pricing_page.faq.q5.answer')}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </MarketingShell>
    );
};

export default PricingPage;
